import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { TranscriptStorageService } from "@/services/conversation/TranscriptStorageService";
import type { VoiceAgentService } from "@/services/integrations/elevenlabs/VoiceAgentService";
import { HandoffAttemptLog, type HandoffTrigger } from "@/services/integrations/twilio/HandoffAttemptLog";
import { useVoiceBridge } from "./useVoiceBridge";

// The Genie's handoff tool only tells it a confirmation prompt is on screen,
// so it has to be told how that prompt was resolved when no call follows.
export const HANDOFF_DECLINED_UPDATE =
  "The visitor closed the call prompt without connecting to a human. No call was made. Carry on helping them.";
export const HANDOFF_FAILED_UPDATE =
  "The call to a human could not be connected. The visitor is still here. Apologise and offer another way to help, such as taking their contact details.";

/** The slice of useVoiceAgentConversation's return this hook needs. */
interface VoiceAgentLike {
  connected: boolean;
  getService: () => VoiceAgentService | null;
  getConversationId: () => string | null;
  endConversation: () => Promise<string | null>;
  sendContextualUpdate: (text: string) => void;
  onHumanHandoffRequested: (callback: () => void) => void;
}

export interface HandoffDeferredActions {
  /** Redirect the visitor once the human call ends. */
  redirect: (url: string) => void;
  /** No redirect pending — decide wrap-up from the transcript. */
  wrapUp: () => void;
}

interface UseHumanHandoffOptions {
  voiceAgent: VoiceAgentLike;
  /** agent.id — required to mint the bridge token. */
  agentId?: string;
  /** agent.voice_bridge_enabled — dormant unless set (default false). */
  enabled: boolean;
  /** agent.voice_bridge_show_button — clients can hide the manual button while keeping the AI tool. */
  showButton?: boolean;
  /**
   * Full page only: when the human call ends, run the end-of-AI-conversation
   * action that was deferred while the Twilio call was starting. The embed
   * leaves this unset — it has no wrap-up/forms/redirect flow.
   */
  onDeferredEnd?: (actions: HandoffDeferredActions) => void;
}

/**
 * Live voice handoff to a human, via Twilio Voice JS SDK — fully additive.
 * Two trigger paths (the AI's client tool + the manual button) both call
 * `confirmHandoff`. The token is minted before the AI session is torn down so
 * a failed bridge leaves the visitor in their existing conversation rather
 * than stranded with neither.
 *
 * `handoffInProgressRef` is exposed because the caller's onConversationEnd
 * reads it to defer its own wrap-up/forms/redirect until the human call ends.
 */
export const useHumanHandoff = ({
  voiceAgent,
  agentId,
  enabled,
  showButton = true,
  onDeferredEnd,
}: UseHumanHandoffOptions) => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const voiceBridge = useVoiceBridge();
  const handoffInProgressRef = useRef(false);
  const attemptLogRef = useRef<HandoffAttemptLog | null>(null);
  const confirmedRef = useRef(false);

  // The deferred actions close over page state, so the caller rebuilds them
  // every render — keep the latest in a ref so the bridge-status effect below
  // always calls current versions without re-subscribing.
  const deferredActionsRef = useRef<HandoffDeferredActions | null>(null);

  const showHandoffButton = enabled && showButton;

  const beginAttempt = useCallback(
    (trigger: HandoffTrigger) => {
      if (!agentId) return null;
      const attempt = new HandoffAttemptLog({
        agentId,
        conversationId: voiceAgent.getConversationId(),
        trigger,
      });
      attemptLogRef.current = attempt;
      return attempt;
    },
    [agentId, voiceAgent],
  );

  const showPromptFor = useCallback(
    (trigger: HandoffTrigger) => {
      confirmedRef.current = false;
      beginAttempt(trigger)?.record("prompt_shown");
      setShowPrompt(true);
    },
    [beginAttempt],
  );

  // AI client tool — the Genie itself can request a human handoff.
  useEffect(() => {
    if (!enabled) return;
    voiceAgent.onHumanHandoffRequested(() => showPromptFor("ai_tool"));
  }, [enabled, voiceAgent, showPromptFor]);

  const confirmHandoff = useCallback(async () => {
    if (!agentId) return;
    setError(null);
    confirmedRef.current = true;
    const attempt = attemptLogRef.current ?? beginAttempt("button");
    attempt?.record("confirmed", "success", { ai_session_connected: voiceAgent.connected });
    try {
      // The AI session is only torn down once the call is real — if the token,
      // the Twilio connection or the microphone fails, the visitor stays in
      // their existing conversation instead of being stranded with neither.
      // The stored transcript lets the server route the call to the
      // escalation number that matches what the visitor needs.
      const callAccepted = await voiceBridge.startBridge(
        agentId,
        TranscriptStorageService.getHandoffMessages(voiceAgent.getConversationId()),
        attempt ?? undefined,
      );
      setShowPrompt(false);
      if (!callAccepted) return;
      if (voiceAgent.connected) {
        handoffInProgressRef.current = true;
        // Note why the session ended so the transcript/analysis reflects a
        // human handoff rather than reading as an ordinary hangup.
        voiceAgent.sendContextualUpdate(
          "The user requested to speak with a human and was connected to a live phone call. This conversation is ending now.",
        );
        await voiceAgent.endConversation();
        attempt?.record("ai_session_ended");
      }
    } catch (err) {
      console.error("Failed to start human handoff:", err);
      setError(err instanceof Error ? err.message : "Couldn't connect you to a human. Please try again.");
      if (voiceAgent.connected) voiceAgent.sendContextualUpdate(HANDOFF_FAILED_UPDATE);
    }
  }, [agentId, voiceBridge, voiceAgent, beginAttempt]);

  // Once the human call itself ends, run whatever the AI session's end
  // deferred while the Twilio call was starting (full page only).
  useEffect(() => {
    if (!handoffInProgressRef.current) return;
    if (voiceBridge.bridgeStatus !== "ended" && voiceBridge.bridgeStatus !== "failed") return;
    handoffInProgressRef.current = false;
    onDeferredEnd?.(deferredActionsRef.current ?? {
      redirect: () => {},
      wrapUp: () => {},
    });
    // deferredActionsRef always reflects the latest caller-supplied callbacks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceBridge.bridgeStatus]);

  const hangupHandoff = useCallback(() => {
    voiceBridge.endBridge();
  }, [voiceBridge]);

  // Mid-call failures (after the confirmation prompt has already closed)
  // surface as a toast instead of reopening the prompt — the AI session is
  // already gone.
  useEffect(() => {
    if (voiceBridge.bridgeStatus === "failed" && voiceBridge.bridgeError && !showPrompt) {
      toast.error(voiceBridge.bridgeError.message || "The call couldn't connect.");
    }
  }, [voiceBridge.bridgeStatus, voiceBridge.bridgeError, showPrompt]);

  const closePrompt = useCallback(() => {
    setShowPrompt(false);
    setError(null);
    if (!showPrompt) return;
    attemptLogRef.current?.record("prompt_closed", "success", {
      confirmed: confirmedRef.current,
      bridge_status: voiceBridge.bridgeStatus,
    });
    // Closed without ever confirming: the Genie was told a prompt is on
    // screen, so it needs to hear the visitor walked away from it.
    if (!confirmedRef.current && voiceAgent.connected) {
      voiceAgent.sendContextualUpdate(HANDOFF_DECLINED_UPDATE);
    }
  }, [showPrompt, voiceBridge.bridgeStatus, voiceAgent]);

  return {
    voiceBridge,
    showPrompt,
    openPrompt: () => showPromptFor("button"),
    closePrompt,
    error,
    showHandoffButton,
    handoffInProgressRef,
    deferredActionsRef,
    confirmHandoff,
    hangupHandoff,
  };
};
